import { useCallback, useMemo, useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { RecaptchaAction } from '@linktivity/ars-neo';
import {
  Button,
  Form,
  Input,
  type FormValues,
  type Rules
} from '@linktivity/link-ui';
import { EMAIL_REGEX, PASSWORD_REGEX } from '@link/share/constants';
import { BaseAuth } from '@link/share/stores';
import { getRecaptchaToken } from '@link/share/vendors';
import ShareAuth from '@link/share/vendors/firebase';
import styles from './login.module.css';

interface SignupProps {
  initialEmail?: string;
  auth: BaseAuth;
  firebase: ShareAuth;
  onBack?: () => void;
  onAuthSuccess?: () => void | Promise<void>;
}

const Signup = ({
  initialEmail = '',
  auth,
  firebase,
  onBack,
  onAuthSuccess
}: SignupProps) => {
  const [formValues, setFormValues] = useState<FormValues>({
    email: initialEmail,
    emailConfirm: '',
    password: ''
  });
  const [valid, setValid] = useState<boolean>(true);
  const [loading, setLoading] = useState<boolean>(false);
  const { t } = useTranslation();

  const rules: Rules = useMemo(
    () => ({
      email: [
        {
          required: true,
          message: t('share.validation.required', {
            field: t('share.login.email')
          }),
          trigger: ['change', 'blur']
        },
        {
          pattern: EMAIL_REGEX,
          message: t('share.validation.invalid', {
            field: t('share.login.email')
          }),
          trigger: 'blur'
        }
      ],
      emailConfirm: [
        {
          required: true,
          message: t('share.validation.required', {
            field: t('share.login.emailConfirm')
          }),
          trigger: ['change', 'blur']
        },
        {
          pattern: EMAIL_REGEX,
          message: t('share.validation.invalid', {
            field: t('share.login.emailConfirm')
          }),
          trigger: 'blur'
        },
        {
          validator: (_, value) => {
            return value === formValues.email;
          },
          message: t('share.validation.emailNotMatch'),
          trigger: ['change', 'blur']
        }
      ],
      password: [
        {
          required: true,
          message: t('share.validation.required', {
            field: t('share.login.password')
          }),
          trigger: ['change', 'blur']
        },
        {
          pattern: PASSWORD_REGEX,
          message: t('share.validation.password'),
          trigger: 'blur'
        }
      ]
    }),
    [t, formValues.email]
  );

  useEffect(() => {
    if (initialEmail) {
      setFormValues(prev => ({
        ...prev,
        email: initialEmail
      }));
    }
  }, [initialEmail]);

  const submitForm = useCallback(async () => {
    setLoading(true);
    const { email, password } = formValues;
    firebase
      .register(email, password)
      .then(async credential => {
        if (!credential) return;
        // Email verification reads the user from the store: set it here rather
        // than rely on Firebase notifying onAuthStateChanged before resolving.
        auth.setUser(credential.user);
        if (onAuthSuccess) {
          await onAuthSuccess();
        } else {
          try {
            const recaptchaToken = await getRecaptchaToken(
              RecaptchaAction.RecaptchaActionSignup
            );
            await auth.updateToken(credential, recaptchaToken);
          } catch (error) {
            firebase.showErrorMessage(error);
          }
        }
      })
      .finally(() => {
        setLoading(false);
        setValid(true);
      });
  }, [auth, firebase, formValues, onAuthSuccess]);

  return (
    <div className={styles.content}>
      <Form
        className={styles.form}
        values={formValues}
        onValuesChange={setFormValues}
        onValidateChange={setValid}
        rules={rules}
        onSubmit={submitForm}
        showRequired={false}
      >
        <Form.Item label={t('share.login.email')} field="email" htmlFor="email">
          <Input id="email" disabled={!!initialEmail} />
        </Form.Item>
        <Form.Item
          label={t('share.login.emailConfirm')}
          field="emailConfirm"
          htmlFor="emailConfirm"
        >
          <Input id="emailConfirm" />
        </Form.Item>
        <Form.Item
          label={t('share.login.password')}
          field="password"
          htmlFor="password"
        >
          <Input type="password" id="password" />
        </Form.Item>
        <Form.Item>
          <Button type="submit" disabled={!valid} loading={loading}>
            {t('share.login.newAccount')}
          </Button>
        </Form.Item>
        {onBack && (
          <Form.Item>
            <Button type="button" variant="outlined" onClick={onBack}>
              {t('share.common.return')}
            </Button>
          </Form.Item>
        )}
      </Form>
    </div>
  );
};

export default Signup;
